<template>
  <label class="g-switch" :class="{ disabled }" :for="buttonID">
    <input
      v-model="proxyChecked"
      :id="buttonID"
      :value="value"
      :type="type"
      v-bind="{ name, disabled, ...$attrs }"
      @change="$emit('updated', $event)"
    />
    <div class="track">
      <div class="flex-spacer"></div>
      <div class="thumb">
        <slot name="thumb" />
      </div>
    </div>
    <span class="label" :class="labelPosition">
      <!-- @slot used for inserting label. Will default to label prop then value if label not provided -->
      <slot>
        {{ label || value }}
      </slot>
    </span>
  </label>
</template>

<script>
/**
 * @version 1.0.0
 */
export default {
  name: 'g-switch',
  inheritAttrs: false,
  model: {
    prop: 'checked',
    event: 'change',
  },
  props: {
    label: {
      type: String,
      default: '',
    },
    labelPosition: {
      type: String,
      default: 'after',
    },
    type: {
      type: String,
      default: 'checkbox',
      validator: (value) => ['checkbox', 'radio'].includes(value),
    },
    id: {
      type: String,
      default: null,
    },
    checked: {
      type: [Boolean, String, Array],
      default: false,
    },
    value: {
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: null,
    },
  },
  computed: {
    proxyChecked: {
      get() {
        if (this.checked === this.value) {
          if (this.type === 'radio') {
            return this.value;
          }
          return true;
        }
        return this.checked;
      },
      set(val) {
        this.$emit('change', val);
      },
    },
    buttonID() {
      return this.id || `g-switch-${this._uid}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.g-switch {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
  .label {
    &.after {
      margin-left: 5px;
    }
    &.before {
      order: -1;
      margin-right: 5px;
    }
  }
  .track {
    position: relative;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    width: 55px;
    height: 24px;
    background-color: $fca-gray;
    border-radius: 9999px;
    transition: background-color 200ms;
    will-change: background-color;
    .thumb {
      display: flex;
      justify-content: center;
      align-items: center;
      box-sizing: border-box;
      width: 20px;
      height: 20px;
      margin: 0 2px;
      content: '';
      background-color: $primary-white;
      border-radius: 9999px;
    }
    .flex-spacer {
      transition: flex 200ms;
      will-change: flex;
    }
  }
  input {
    position: absolute;
    top: 0;
    left: 0;
    margin: 0;
    padding: 0;
    /* tweak size and position if needed */
    width: 1em;
    height: 1em;
    /* hide it visually */
    opacity: 0.00001;
    &:checked + .track {
      background-color: $primary;
      // This one's a little goofy. I'm moving the track by setting the flex-grow on this spacer once checked
      // It just allows for the thumb to be customized with less hardcoded parameters
      .flex-spacer {
        flex: 1;
      }
    }
  }
  .label {
    display: block;
    margin-left: 5px;
    white-space: nowrap;
  }
  &.label-before {
    .label {
      order: -1;
      margin-right: 5px;
      margin-left: 0;
    }
  }
}
</style>
